import { useMutation } from "@apollo/react-hooks";
import gql from "graphql-tag";
import { useState } from "react";
import Permissions from "../configs/permissions";
import usePermissions from "../hooks/permissions";
import {
  TableContainer,
  TableView,
  TCol,
  TColBorderless,
  TContent,
  THCol,
  THeading,
  TRow,
} from "../mood-ui/DataTable";
import PageSpinner from "../mood-ui/PageSpinner";
import { SURVEY_MODEL_NAME } from "./../configs/constants";
import { createSurveyMutation } from "./../graphql/mutations/vabooSurveyMutations";
import useBaseFieldSetters from "./../hooks/baseFieldSetters";
import useBaseNavigation from "./../hooks/baseNavigation";
import useDataProvider from "./../hooks/dataProvider";
import useFormErrors from "./../hooks/formErrors";
import useHyperState from "./../hooks/hyperState";
import {
  AddButton,
  CancelButton,
  DeleteButton,
  FormControls,
  SaveButton,
} from "./../mood-ui/Controls";
import {
  MultiSelectField,
  RichTextEditorField,
  SelectField,
  TextField,
} from "./../mood-ui/Fields";
import {
  BlockContainer,
  Col,
  FormContainer,
  FormData,
  PageContainer,
  Row,
} from "./../mood-ui/Layout";
import { Heading, ItemsNotFound } from "./../mood-ui/Typography";

const CREATE_SURVEY = gql(createSurveyMutation);

const SurveyCreate = () => {
  const { protectPage } = usePermissions();
  protectPage(Permissions.Mutation.CreateSurvey);
  const { fieldError, resolveMutationErrors, clearError } = useFormErrors();

  const { goBack } = useBaseNavigation(SURVEY_MODEL_NAME);

  const [surveyTypes] = useDataProvider("surveyTypes");

  const [questions] = useDataProvider("questions", {
    filters: { status: ["Published"], usageType: ["Survey"] },
  });

  const {
    state: formData,
    setState: setFormData,
    stateMethods,
  } = useHyperState("formData", {
    title: "",
    reusable: false,
    description: "",
    survey_type_id: 0,
  });

  const [questionIds, setQuestionIds] = useState([]);

  const {
    setFieldValue: handleFieldOnChange,
    toggleCheckbox: handleToggleCheckbox,
  } = useBaseFieldSetters(formData, stateMethods, clearError);

  const [
    createSurvey,
    { data: createResult, loading: isCreating, error: creationError },
  ] = useMutation(CREATE_SURVEY, {
    variables: { ...formData },
    onCompleted: (result) => {
      goBack();
    },
    onError: (e) => {
      resolveMutationErrors(e);
    },
  });

  const handleAddQuestion = () => {
    const newId = "";
    setQuestionIds((prev) => [...prev, newId]);
  };

  const handleUpdateQuestions = (index, qid) => {
    setQuestionIds((prev) => {
      const updated = [...prev];
      updated[index] = qid;
      return updated;
    });
  };

  const handleDeleteQuestion = (index) => {
    setQuestionIds((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSave = () => {
    const data = { ...formData };
    data.question_ids = questionIds.filter(Boolean).join(",");
    createSurvey({ variables: data });
  };

  if (!formData) return <div></div>;
  if (!questions.length) return <PageSpinner />;

  return (
    <PageContainer>
      <BlockContainer raised>
        <Row tweaks="border-b border-[#dfe2e8] pb-8 mt-0 mx-5">
          <Col width={12}>
            <Heading
              inset={true}
              text="Create a survey"
              description="Set up your survey questions, finalize and publish the survey, then assign it to clients to begin collecting responses."
            />
          </Col>
        </Row>
        <FormContainer>
          <FormData>
            <Row tweaks="border-b border-[#dfe2e8] py-7 mx-5">
              <Col width={3}>
                <Row>
                  <Col width={12}>
                    <Heading
                      size="md"
                      text="Survey details"
                      description="Main survey details."
                    />
                  </Col>
                </Row>
              </Col>
              <Col
                width={1}
                extraStyles="border-r border-[#dfe2e8] h-full justify-self-center w-full"
              ></Col>
              <Col width={8}>
                <Row>
                  <Col width={8}>
                    <TextField
                      name="title"
                      label="Title"
                      value={formData.title}
                      onChange={handleFieldOnChange}
                      error={fieldError("title")}
                      isRequired={true}
                      minlength={3}
                      maxlength={48}
                      description=""
                    />
                  </Col>
                  <Col width={4}>
                    <SelectField
                      value={formData.survey_type_id}
                      values={surveyTypes}
                      label="Survey Type"
                      name="survey_type_id"
                      onChange={(e) => handleFieldOnChange(e, "int")}
                      valueKey="id"
                      labelKey="name"
                      error={fieldError("survey_type_id")}
                      prependOption="Select Type"
                      isRequired={true}
                    />
                  </Col>
                  <Col width={12}>
                    <RichTextEditorField
                      name="description"
                      label="Description"
                      value={formData?.description || ""}
                      onChange={handleFieldOnChange}
                      error={fieldError("description")}
                      isRequired={false}
                      maxlength="1024"
                      rows={5}
                    />
                  </Col>
                </Row>
              </Col>
            </Row>

            {/* Questions */}
            <Row tweaks="border-b border-[#dfe2e8] py-7 mx-5">
              <Col width={3}>
                <Row>
                  <Col width={12}>
                    <Heading
                      size="md"
                      text="Questions"
                      description="Assign questions that should be included into this survey. Only published questions are available."
                    />
                  </Col>
                </Row>
              </Col>
              <Col
                width={1}
                extraStyles="border-r border-[#dfe2e8] h-full justify-self-center w-full"
              ></Col>
              <Col width={8}>
                {/* + Add Answer — зліва, як у QuestionCreate */}
                <Row>
                  <Col width={12} extraStyles="flex items-center gap-2 mb-3">
                    <AddButton icon onClick={handleAddQuestion} />
                    <span className="text-sm text-[#6b7280]">Add Answer</span>
                  </Col>
                </Row>

                <Row tweaks="gap-0 sm:grid-cols-1 py-0 px-px">
                  <Col width={12} extraStyles="sm:col-span-1">
                    {questionIds.length > 0 && (
                      <TableContainer tweaks="relative mt-0">
                        <TableView>
                          <THeading>
                            <TRow>
                              <THCol tweaks="text-left" value="" />
                              <THCol
                                width="w-[38.5px]"
                                tweaks="px-0.5 py-0.5"
                              />
                            </TRow>
                          </THeading>
                          <TContent>
                            {questionIds.map((qid, index) => (
                              <TRow key={index}>
                                <TColBorderless tweaks="px-0.5 py-0.5">
                                  <SelectField
                                    value={qid}
                                    values={questions}
                                    onChange={(e) =>
                                      handleUpdateQuestions(
                                        index,
                                        e.target.value
                                      )
                                    }
                                    valueKey="id"
                                    labelKey="dropdown_title"
                                    prependOption="Select Question"
                                    isRequired={false}
                                    borderless={true}
                                  />
                                </TColBorderless>
                                <TColBorderless
                                  width="w-[38.5px]"
                                  tweaks="px-0.5 py-0.5"
                                >
                                  {questionIds.length > 0 && (
                                    <DeleteButton
                                      icon
                                      onClick={() =>
                                        handleDeleteQuestion(index)
                                      }
                                    />
                                  )}
                                </TColBorderless>
                              </TRow>
                            ))}
                          </TContent>
                        </TableView>
                      </TableContainer>
                    )}
                    {questionIds.length === 0 && (
                      <div className="relative">
                        <div className="-mb-14s">
                          <ItemsNotFound
                            text="No questions yet"
                            description="Click + Add Answer to add questions."
                          />
                        </div>
                      </div>
                    )}
                  </Col>
                </Row>
              </Col>
            </Row>
          </FormData>

          <FormControls>
            <CancelButton onClick={goBack} />
            <SaveButton onClick={handleSave} loading={isCreating} />
          </FormControls>
        </FormContainer>
      </BlockContainer>
    </PageContainer>
  );
};

export default SurveyCreate;
